import React, { useState, useEffect } from 'react';
import { useLocation as useRouterLocation } from 'react-router-dom';
import { FileText, X, Loader2, Search, ChevronRight, User, Calendar, Cloud, Wrench, Package, AlertTriangle, Truck, Users, FileDown } from 'lucide-react';
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';

interface DailyReport {
  report_id: number;
  job_id: number;
  job_name: string;
  bradanna_job_number: string;
  foreman_first_name: string;
  foreman_last_name: string;
  report_date: string;
  created_at: string;
  status: string;
  weather?: string;
  temperature?: number;
  work_completed_description?: string;
  issues?: string;
  photo_urls?: string;
  job_needs?: string | null;
  job_needs_other_text?: string | null;
  silt_fence_condition?: string;
  silt_fence_notes?: string;
  // Truck ticket counts
  fill_loads?: number;
  rock_loads?: number;
  baserock_loads?: number;
  asphalt_loads?: number;
  other_loads?: number;
  // Haul off counts
  fill_dirt_truck?: number;
  baserock_truck?: number;
  asphalt_truck?: number;
  asphalt_millings_truck?: number;
  concrete_truck?: number;
  misc_truck?: number;
  concrete_dumpster?: number;
  demolition_dumpster?: number;
  vegetation_dumpster?: number;
  // Equipment issues
  equipment_issues_count?: number;
  // Subcontractors
  subcontractors?: string;
}

// Get passed jobId from navigation
const usePassedJobId = () => {
  const routerLocation = useRouterLocation();
  return routerLocation.state?.jobId as number | undefined;
};

export const DailyReportViewer: React.FC = () => {
  const passedJobId = usePassedJobId();

  const [reports, setReports] = useState<DailyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReport, setSelectedReport] = useState<DailyReport | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [jobInfo, setJobInfo] = useState<{ job_name: string; bradanna_job_number: string } | null>(null);
  const [todayCount, setTodayCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingReportId, setLoadingReportId] = useState<number | null>(null);

  // Open PDF for a report
  const openPdf = async (reportId: number) => {
    try {
      setLoadingPdf(true);
      setLoadingReportId(reportId);
      const token = localStorage.getItem('bfa_token');

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/daily-reports/generate-pdf/${reportId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to load PDF: ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      // Open in new window/tab
      window.open(url, '_blank');

      // Clean up blob URL after a delay
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('Error loading PDF:', error);
      alert('Failed to load PDF');
    } finally {
      setLoadingPdf(false);
      setLoadingReportId(null);
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatFullDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Load reports
  useEffect(() => {
    const fetchReports = async () => {
      if (!passedJobId) {
        setError('No job selected');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await api.get(`${API_CONFIG.ENDPOINTS.DAILY_REPORTS}?job_id=${passedJobId}`);
        if (response.data.success) {
          const reportData = response.data.data;
          setReports(reportData);
          setTotalCount(reportData.length);

          // Count today's reports
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const todaysReports = reportData.filter((r: DailyReport) => {
            const reportDate = new Date(r.report_date);
            reportDate.setHours(0, 0, 0, 0);
            return reportDate.getTime() === today.getTime();
          });
          setTodayCount(todaysReports.length);

          if (reportData.length > 0) {
            setJobInfo({
              job_name: reportData[0].job_name,
              bradanna_job_number: reportData[0].bradanna_job_number
            });
          }
        }
      } catch (err) {
        console.error('Error fetching reports:', err);
        setError('Failed to load reports');
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [passedJobId]);

  // Filter reports by search
  const filteredReports = reports
    .filter(r => {
      if (!searchTerm.trim()) return true;
      const search = searchTerm.toLowerCase();
      const foremanName = `${r.foreman_first_name} ${r.foreman_last_name}`.toLowerCase();
      return foremanName.includes(search);
    })
    .sort((a, b) => new Date(b.report_date).getTime() - new Date(a.report_date).getTime());

  // Get total truck loads
  const getTotalTruckLoads = (report: DailyReport): number => {
    return (report.fill_loads || 0) +
           (report.rock_loads || 0) +
           (report.baserock_loads || 0) +
           (report.asphalt_loads || 0) +
           (report.other_loads || 0);
  };

  // Get total haul off
  const getTotalHaulOff = (report: DailyReport): number => {
    return (report.fill_dirt_truck || 0) +
           (report.baserock_truck || 0) +
           (report.asphalt_truck || 0) +
           (report.asphalt_millings_truck || 0) +
           (report.concrete_truck || 0) +
           (report.misc_truck || 0) +
           (report.concrete_dumpster || 0) +
           (report.demolition_dumpster || 0) +
           (report.vegetation_dumpster || 0);
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    if (status === 'approved') {
      return (
        <span style={{
          padding: '2px 8px',
          backgroundColor: '#dcfce7',
          color: '#166534',
          fontSize: '11px',
          fontWeight: '600',
          borderRadius: '4px'
        }}>
          Approved
        </span>
      );
    }
    return (
      <span style={{
        padding: '2px 8px',
        backgroundColor: '#fef3c7',
        color: '#92400e',
        fontSize: '11px',
        fontWeight: '600',
        borderRadius: '4px'
      }}>
        Pending
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <Loader2 size={40} style={{ color: '#3b82f6', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#64748b', marginTop: '16px' }}>Loading reports...</p>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#dc2626' }}>
        {error}
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', paddingBottom: '100px' }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#ffffff',
        padding: '16px 20px',
        borderBottom: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <h1 style={{ color: '#1e293b', fontSize: '20px', fontWeight: '700', margin: 0 }}>
          Daily Reports
        </h1>
        {jobInfo && (
          <p style={{ color: '#64748b', fontSize: '14px', margin: '4px 0 0' }}>
            {jobInfo.job_name} • #{jobInfo.bradanna_job_number}
          </p>
        )}
      </div>

      <div style={{ padding: '16px' }}>
        {/* KPI Stats - Today and Total */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <div style={{
            backgroundColor: '#10b981',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: 'white' }}>{todayCount}</div>
            <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.9)', fontWeight: '500' }}>Today</div>
          </div>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #3b82f6'
          }}>
            <div style={{ fontSize: '32px', fontWeight: '700', color: '#1e293b' }}>{totalCount}</div>
            <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Total</div>
          </div>
        </div>

        {/* Search */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '10px',
          padding: '12px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <Search size={20} color="#64748b" />
          <input
            type="text"
            placeholder="Search by foreman name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: '15px',
              color: '#1e293b',
              backgroundColor: 'transparent'
            }}
          />
          {searchTerm && (
            <X
              size={20}
              color="#94a3b8"
              onClick={() => setSearchTerm('')}
              style={{ cursor: 'pointer' }}
            />
          )}
        </div>

        {/* Report List */}
        {filteredReports.length === 0 ? (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '40px 20px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <FileText size={48} color="#94a3b8" style={{ marginBottom: '16px' }} />
            <p style={{ color: '#64748b', fontSize: '16px', fontWeight: '500' }}>
              {searchTerm ? 'No reports found' : 'No reports for this job'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filteredReports.map(report => (
              <div
                key={report.report_id}
                onClick={() => openPdf(report.report_id)}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '10px',
                  padding: '14px 16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  cursor: loadingPdf ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  opacity: loadingPdf ? 0.7 : 1
                }}
              >
                <div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                    {formatDate(report.report_date)}
                  </div>
                  <div style={{ fontSize: '14px', color: '#64748b', marginTop: '2px' }}>
                    {report.foreman_first_name} {report.foreman_last_name}
                  </div>
                </div>
                <FileDown size={20} color="#10b981" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Loading Overlay */}
      {loadingPdf && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Loader2 size={48} color="white" style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ color: 'white', marginTop: '16px', fontSize: '16px', fontWeight: '500' }}>
            Loading PDF...
          </p>
        </div>
      )}

      {/* Report Detail Modal */}
      {showModal && selectedReport && (
        <div
          onClick={() => setShowModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'flex-end'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <div>
                <h2 style={{ color: 'white', fontSize: '18px', fontWeight: '700', margin: 0 }}>
                  {formatFullDate(selectedReport.report_date)}
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '14px', margin: '4px 0 0' }}>
                  {selectedReport.foreman_first_name} {selectedReport.foreman_last_name}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => openPdf(selectedReport.report_id)}
                  disabled={loadingPdf}
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    cursor: loadingPdf ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: loadingPdf ? 0.7 : 1
                  }}
                >
                  {loadingPdf ? (
                    <Loader2 size={20} color="white" style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <FileDown size={20} color="white" />
                  )}
                  <span style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>PDF</span>
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px',
                    cursor: 'pointer'
                  }}
                >
                  <X size={24} color="white" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '20px', paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))' }}>
              {/* Status & Weather */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px'
              }}>
                {getStatusBadge(selectedReport.status)}
                {selectedReport.weather && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b', fontSize: '14px' }}>
                    <Cloud size={16} />
                    {selectedReport.weather} {selectedReport.temperature && `${selectedReport.temperature}°F`}
                  </div>
                )}
              </div>

              {/* Work Completed */}
              {selectedReport.work_completed_description && (
                <Section title="Work Completed" icon={FileText}>
                  <p style={{ color: '#1e293b', fontSize: '14px', lineHeight: '1.5', margin: 0 }}>
                    {selectedReport.work_completed_description}
                  </p>
                </Section>
              )}

              {/* Issues */}
              {selectedReport.issues && (
                <Section title="Issues / Problems" icon={AlertTriangle} color="#f59e0b">
                  <p style={{ color: '#1e293b', fontSize: '14px', lineHeight: '1.5', margin: 0 }}>
                    {selectedReport.issues}
                  </p>
                </Section>
              )}

              {/* Material Stats */}
              {(getTotalTruckLoads(selectedReport) > 0 || getTotalHaulOff(selectedReport) > 0) && (
                <Section title="Materials" icon={Package}>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <div style={{ flex: 1, textAlign: 'center', padding: '12px', backgroundColor: '#f0fdf4', borderRadius: '8px' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981' }}>
                        {getTotalTruckLoads(selectedReport)}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>Loads In</div>
                    </div>
                    <div style={{ flex: 1, textAlign: 'center', padding: '12px', backgroundColor: '#fff7ed', borderRadius: '8px' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#f59e0b' }}>
                        {getTotalHaulOff(selectedReport)}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>Haul Off</div>
                    </div>
                  </div>
                </Section>
              )}

              {/* Subcontractors */}
              {selectedReport.subcontractors && (
                <Section title="Subcontractors" icon={Users}>
                  <p style={{ color: '#1e293b', fontSize: '14px', lineHeight: '1.5', margin: 0 }}>
                    {selectedReport.subcontractors}
                  </p>
                </Section>
              )}

              {/* Equipment Issues */}
              {selectedReport.equipment_issues_count && selectedReport.equipment_issues_count > 0 && (
                <Section title="Equipment Issues" icon={Wrench} color="#ef4444">
                  <p style={{ color: '#1e293b', fontSize: '14px', margin: 0 }}>
                    {selectedReport.equipment_issues_count} issue(s) reported
                  </p>
                </Section>
              )}

              {/* Silt Fence */}
              {selectedReport.silt_fence_condition && (
                <Section title="Silt Fence" icon={AlertTriangle} color={selectedReport.silt_fence_condition === 'good' ? '#10b981' : '#f59e0b'}>
                  <p style={{ color: '#1e293b', fontSize: '14px', margin: 0, textTransform: 'capitalize' }}>
                    Condition: {selectedReport.silt_fence_condition}
                    {selectedReport.silt_fence_notes && ` - ${selectedReport.silt_fence_notes}`}
                  </p>
                </Section>
              )}

              {/* Job Needs */}
              {selectedReport.job_needs && (
                <Section title="Job Needs for Tomorrow" icon={Truck}>
                  <p style={{ color: '#1e293b', fontSize: '14px', lineHeight: '1.5', margin: 0 }}>
                    {selectedReport.job_needs}
                    {selectedReport.job_needs_other_text && ` - ${selectedReport.job_needs_other_text}`}
                  </p>
                </Section>
              )}

              {/* Photos */}
              {selectedReport.photo_urls && (
                <Section title="Photos" icon={FileText}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedReport.photo_urls.split(',').map((url, idx) => (
                      <img
                        key={idx}
                        src={url.trim()}
                        alt={`Report photo ${idx + 1}`}
                        style={{
                          width: '100px',
                          height: '100px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0'
                        }}
                      />
                    ))}
                  </div>
                </Section>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Section Component
const Section: React.FC<{
  title: string;
  icon: any;
  color?: string;
  children: React.ReactNode;
}> = ({ title, icon: Icon, color = '#3b82f6', children }) => (
  <div style={{
    backgroundColor: '#f8fafc',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '12px'
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '10px'
    }}>
      <Icon size={18} color={color} />
      <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#64748b', margin: 0 }}>
        {title}
      </h4>
    </div>
    {children}
  </div>
);
